/**
 * Starting one replication run.
 *
 * `DatabaseWrapper` holds two different things at once: a replication engine
 * that keeps this device's local PouchDB in step with the remote CouchDB, and a
 * document-access API (get / save / query) that the rest of the app calls. This
 * file holds the largest single piece of the first one — the code that decides
 * which kind of sync to start, builds its options, and wires up the handlers
 * that run for the life of that sync.
 *
 * It deliberately knows nothing about `DatabaseWrapper`. Everything it touches
 * arrives through the `SyncStartContext` object below, which is why the module
 * can be exercised in a unit test with a small fake instead of a whole database
 * wrapper. Keeping the dependency explicit is the point: the list of members on
 * that interface IS the answer to "what does starting a sync actually need?",
 * and it used to be answerable only by reading 550 lines and noting every
 * `this.`.
 *
 * Nothing here changed behaviour when it moved out of `database-wrapper.ts`.
 * The invariants below were already true and are restated because they are easy
 * to break with an edit that looks harmless:
 *
 *  - **The live user-database sync sets the changes-feed `style` per direction.**
 *    The push uses `all_docs` (from `ctx.userDbPushChangesStyle`) so that a
 *    tombstone closing a losing conflict branch is offered to the server at all;
 *    the pull keeps the top-level `main_only`. PouchDB merges the per-direction
 *    object over the top-level options, so the two coexist. Collapsing them back
 *    into one top-level `style` silently stops conflict-closing tombstones from
 *    ever replicating. See `design-docs/sync-integrity.md`.
 *  - **The initial sync runs in two passes** (`SyncPhase.CalendarWindow`, then
 *    `SyncPhase.PriorityWindow`), and only for the user database. Pass 1 ends by
 *    releasing the boot screen and starting pass 2; `setInitialSyncCompleted(true)`
 *    must fire only after pass 2, because that flag is what lets computed task
 *    statuses be written down, and after pass 1 the months behind the calendar
 *    have not arrived yet.
 *  - **`whenCalendarWindowSyncCompleted` is a one-shot** that boot waits on. The
 *    early return for a remote-only or impersonated database releases it, so a
 *    boot where no sync runs at all does not sit behind the timeout.
 *  - **The per-document work in the pull handler stays cheap.** Pulled documents
 *    are handed to the registered listeners once per batch, never per document,
 *    and no replication document is retained. See `design-docs/reactive-sync.md`.
 */

import { DateTime } from 'luxon';
import { AuthRole } from '@cf-shared/couch-auth';
import { PouchDoc, isPouchConflict } from '@cf-shared/data-model/pouch';
import { timeout } from '@cf-shared/shared-util';
import { ACTIVE_TEXT_SEGMENT, buildGlobalCommonLocaleSelector, MangoSelector } from './text-locale';
import { PulledDeletionToSettle } from './database-wrapper-pulled-deletions';
import { resolveExistingSingletonIds } from './database-wrapper-sync-boot-policy';
import { timeoutCausedByFreeze } from './running-clock';
import { isTransientFetchError } from './error-patterns';
import {
  computePullSyncTaskCounters,
  isInitialSyncPhase,
  shouldPreCountInitialSyncDocs,
  SyncPhase,
} from './initial-sync-progress';
import {
  formatPulledFromOtherDeviceLog,
  PULLED_FROM_OTHER_DEVICE_LOG_SAMPLE_SIZE,
} from './sync-telemetry-shaping';
import {
  isNetworkShapedSyncError,
  LastSyncError,
} from './sync-failure-classification';
import { elapsedSinceIsoMs } from './sync-rev-and-time-utils';
import { stopFailedOneShotReplication } from './one-shot-replication';
import type { PulledDocApplyResult } from './database-wrapper';
import type { ClosedHandleOrigin } from './database-recovery.service';

/**
 * Timeout for the singleton-docs existence check at the start of an initial
 * sync (#3898). The live syncHandler is only assigned AFTER this remote call,
 * and startSyncInternal holds syncMutex throughout — so a hung request here
 * wedges boot (dbState:"syncing" + syncHandlerActive:false) and blocks every
 * recovery path. Bounding it lets the boot fall through to the main sync.
 * Matches the 10s timeout already used for remoteDb.info() just below it.
 * Wall time on purpose; a timeout a phone's freeze caused is reported as info (#6096).
 */
const SINGLETON_EXISTS_TIMEOUT_MS = 10_000;

/**
 * The ids an officer's device must never offer to the server when it pushes `global_common`
 * (#5679): every food document, which since #5625 means the curated staples, `food/$STP-...`.
 *
 * An officer syncs the shared databases both ways, so without this a stale or damaged local
 * copy of a staple would be offered back up. The server refuses it — only the upload tooling may
 * write `food/` there (`envs/shared/global_common.design.js`) — and that refusal is the real
 * guard, because an older build ignores what is set here. This keeps a current build from
 * offering the document at all, since a refusal flips a live sync to Offline and marks an
 * initial pass incomplete. The pull is untouched: the officer still downloads the staples.
 */
const GLOBAL_COMMON_UPLOAD_ONLY_ID_PREFIX = 'food/';

/**
 * The Mango clause matching exactly the ids that do NOT start with the prefix above.
 *
 * Written as `$nor` of the prefix's id range, NOT as an `$or` of the two ranges either side of
 * it. The selector it joins already has an `$or` on `_id` (the log window), and PouchDB's Mango
 * evaluator merges two `$or` lists on the same field into an EMPTY `$or` — which matches
 * nothing, so the push would silently offer no documents at all. The replication runs every
 * selector through that evaluator (`matchesSelector` → `massageSelector`).
 */
const GLOBAL_COMMON_PUSHABLE_ID_CLAUSE = {
  $nor: [
    { _id: { $gte: GLOBAL_COMMON_UPLOAD_ONLY_ID_PREFIX, $lt: GLOBAL_COMMON_UPLOAD_ONLY_ID_PREFIX + '\ufff0' } },
  ],
};

/** the same test as the clause above, for the live sync's function filter */
function isPushableToGlobalCommon(docId: string): boolean {
  return !docId.startsWith(GLOBAL_COMMON_UPLOAD_ONLY_ID_PREFIX);
}

/** one entry of the same-device revision diagnostic the pull loop collects */
export interface UnexpectedSyncRev {
  docId: string;
  pulledRev: string;
  expectedRev: string;
  isOwnSession: boolean;
}

/**
 * Everything starting a sync needs from the database wrapper around it.
 *
 * Grouped on purpose, and a new member goes in the group it belongs to: state
 * this module only reads, constants that live on the wrapper, state it writes
 * back, and things it asks the wrapper to do. A member here is not an
 * invitation to add more — each one is a piece of the document-access side that
 * the sync engine has not been separated from yet.
 */
export interface SyncStartContext {
  // ---- state this module only reads ----

  /** true for a database that is never replicated to a local copy */
  readonly remoteOnly: boolean;
  /** set while a coach or admin is impersonating another account */
  readonly temporaryDb: any;
  readonly remoteDb: any;
  readonly localDb: any;
  /** true from the moment the wrapper's shutdown STARTS, and never cleared again.
   *  Checking `remoteDb` alone is not enough: the shutdown nulls the handles only
   *  at the very end, so they stay set for the whole unbounded wait in between.
   *  See design-docs/pass-through-db.md, "Which databases a sweep may act on". */
  readonly isShuttingDown: boolean;
  /** above 0 while a restore or purge has detached `remoteDb` on purpose, so a missing
   *  handle is expected rather than a fault */
  readonly remoteDetachedForOwnWork: number;
  /** the name used in log lines and telemetry, e.g. `user` */
  readonly internalDatabaseIdentifier: string;
  /** the local PouchDB name, used to key the background-sync-complete marker */
  readonly databaseIdLocal: string;
  readonly initialSyncCompleted: boolean | undefined;
  /** the singleton documents this database keeps, by document id */
  readonly singletons: { [docId: string]: unknown };
  /** the identity map, read here only to find a body before it is evicted */
  readonly documentCache: {
    get(docId: string): { ref: { deref(): any } } | undefined;
    delete(docId: string): boolean;
  };
  /** how many pulled-doc listeners are registered right now */
  readonly pulledDocListenerCount: number;
  /** where the pull loop records same-device revision surprises */
  readonly unexpectedSyncRevs: UnexpectedSyncRev[];
  /** the one-shot boot waits on; released by pass 1 and by the early return */
  readonly whenCalendarWindowSyncCompleted: { resolve(value: boolean | undefined): void };
  readonly auth: { readonly couch: unknown; getAuthRole(): AuthRole };
  readonly logging: {
    trackSilentInfo(name: string, context: { [key: string]: any }): void;
    trackSilentProblem(name: string, context: { [key: string]: any }): void;
  };
  readonly databaseRecovery: {
    /** returns true when it took the error over and started a recovery.
     *  `origin` names this call site on the report a closed database handle produces (#5306). */
    interceptDatabaseError(err: any, origin?: ClosedHandleOrigin): boolean;
  };
  readonly telemetry: { trackEvent(name: string, params: { [key: string]: any }): void } | null;
  /** the wrapper itself, published verbatim in the progress event payload */
  readonly databaseRef: unknown;

  // ---- constants that live on the wrapper ----

  /** documents no sync ever moves: server-side design docs and device-local docs */
  syncExcludedDocIds(): readonly string[];
  /** documents each device keeps its own copy of and never replicates (#4212) */
  readonly deviceLocalDocIds: readonly string[];
  /** log-id cutoff for the first initial pass (the task calendar's window) */
  getCalendarSyncCutoffDate(): string;
  /** log-id cutoff for the second initial pass (the full priority window) */
  getPrioritySyncCutoffDate(): string;
  /** changes-feed style for the PUSH direction of the live user-db sync */
  readonly userDbPushChangesStyle: string;
  readonly dbBackgroundSyncCompleteKeyPrefix: string;

  // ---- state this module writes back ----

  syncHandler: any;
  /**
   * The singleton pre-sync's replication, while one is running, and `null` otherwise.
   *
   * It is a SECOND replication on the same two handles, separate from `syncHandler`, and it
   * is published here for one reason: so `cancelSync` can cancel it. See the comment on
   * `startSingletonDocSync` for what happened while nothing could (#5306).
   */
  singletonSyncHandler: any;
  syncHandlerIsInitial: boolean;
  lastSyncError: LastSyncError | null;
  syncTasksCount: number;
  syncTasksCompleted: number;
  syncGeneration: number;
  initialSyncDocsToPull: number | null;
  initialSyncDocsReadBaseline: number;
  lastPullDocsRead: number;
  lastSyncActivityTime_ms: number;
  initialSyncSkippedEmpty: boolean;
  realtimeListenerRestarts: number;
  consecutiveAuthErrors: number;

  // ---- things it asks the wrapper to do ----

  /**
   * This device's id. Taken from the context rather than imported straight from
   * `environments/environment-helpers`, because that module reaches the
   * Capacitor and environment layer, which cannot load outside a browser.
   * Importing it here would make this whole file impossible to unit-test, which
   * is the one thing the extraction was for.
   */
  getDeviceId(): string;
  isUserDb(): boolean;
  isGlobalCommonDb(): boolean;
  replicationFilter(doc: PouchDoc): boolean;
  serializeSyncError(err: any): any;
  setInitialSyncCompleted(value: boolean): void;
  /** One initial-sync round failed and will be retried: reports `false` to everyone waiting,
   *  but keeps reads on the server while online (#6554). Use this, not
   *  `setInitialSyncCompleted(false)`, for a failure the retry loop will try again. */
  recordInitialSyncRoundFailed(): void;
  /** `coveredEveryServerSingleton` false: the pre-sync could not learn which singletons the
   *  server holds, so it synced nothing (#6248). Defaults to true. */
  setInitialSingletonSyncCompleted(value: boolean, coveredEveryServerSingleton?: boolean): void;
  startSync(initialOrPhase: boolean | SyncPhase): Promise<void> | void;
  cancelSync(): void;
  /**
   * Write out every diagnostic this sync has been accumulating (today: the same-device
   * revisions it pulled back).
   *
   * There is ONE method for all of them on purpose. A replication can end in three ways
   * (`complete`, `paused`, `error`) and each one flushes, so listing the diagnostics
   * separately at each of the three meant a new diagnostic had to be remembered in three
   * places — and one left out of `error` would silently lose every count from a sync that
   * ended badly. Add a new sync diagnostic to the wrapper's implementation of THIS method
   * and it reaches all three endings.
   */
  flushSyncDiagnostics(): void;
  isNonRetryableSyncError(err: any): boolean;
  handleSyncAuthError(err: any, source: 'denied' | 'error', logHeader: string): void;
  applyPulledDocToCache(doc: any): PulledDocApplyResult;
  notifyDocWatchers(docId: string): void;
  deliverPulledDocsToListeners(docs: any[]): void;
  settlePulledDeletionsAsync(tombstones: PulledDeletionToSettle[]): Promise<void>;
  stopRealTimeListener(): void;
  startRealTimeListener(): void;
  shouldRestartWithInitialSync(): boolean;
  reportStaleUnflushedChangeIfNeeded(): void;
  reportPushStallIfNeeded(): void;
  countInitialSyncDocsAsync(phase: SyncPhase, logHeader: string): Promise<void>;
  /** publish the sync-progress event with this payload, exactly as given */
  publishSyncProgress(payload: { [key: string]: any }): void;
  /**
   * The three state transitions this module makes. They are separate methods
   * rather than one `updateState(state)` so that this file does not have to
   * import the `DatabaseState` enum's VALUES from `database-wrapper.ts`, which
   * would make the two modules import each other in a circle.
   */
  updateStateSyncing(): void;
  updateStateReady(): void;
  updateStateOffline(): void;
  /** whether the wrapper's published state is currently Ready */
  isStateReady(): boolean;
}

/**
 * True once the wrapper's database handles have been taken away underneath a
 * sync start that had already begun — a sign-out, an account switch, a restore
 * or a data deletion landing while one of this module's awaits is outstanding.
 *
 * Only `checkSyncStartEntryGuards` asks this directly, because its order relative
 * to the other entry checks is load-bearing. Everywhere else asks
 * `syncStartNoLongerWanted` below, which asks this AND the shutdown flag.
 *
 * Why the window exists, why `cancelSync()` does not close it, and why the
 * report below must stay at the INFO tier: `design-docs/pass-through-db.md`,
 * "A sync start can outlive the handles it was given".
 */
function databaseHandlesAreGone(ctx: SyncStartContext): boolean {
  return ctx.localDb == null || ctx.remoteDb == null;
}

/**
 * Record that a sync start was abandoned because the wrapper's handles had gone
 * under it. Called by `syncStartNoLongerWanted` and by the entry guard; nothing
 * else picks this report over its sibling below.
 *
 * The INFO tier is load-bearing rather than merely defensible — see the doc
 * section named above `databaseHandlesAreGone`.
 */
function reportSyncStartAbandoned(ctx: SyncStartContext, phase: SyncPhase, where: string): void {
  ctx.logging.trackSilentInfo('SyncStartAbandonedOnShutdown', {
    db: ctx.internalDatabaseIdentifier,
    phase,
    where,
    localDbGone: ctx.localDb == null,
    remoteDbGone: ctx.remoteDb == null,
  });
}

/**
 * Record that a sync start was refused because the wrapper's shutdown had already
 * begun while both handles were still set. Called by `syncStartNoLongerWanted`
 * and by the entry guard; `syncStartNoLongerWanted` is where the difference
 * between this report and its sibling above is explained.
 *
 * The INFO tier is load-bearing for the same reason it is on the sibling — see
 * `design-docs/pass-through-db.md`, "A sync start can outlive the handles it was
 * given": `trackSilentProblem` writes `console.error`, and the E2E runner counts
 * an error console line as a failed scenario. It is also the honest tier, because
 * the shutdown is deliberate and the guard IS the repair. It is reported at all,
 * rather than only logged, because a sync somebody asked for did not happen.
 */
function reportSyncStartSkippedForShutdown(
  ctx: SyncStartContext, phase: SyncPhase, logHeader: string, where: string,
): void {
  console.log(`${logHeader} not starting — the database is shutting down (${where})`);
  ctx.logging.trackSilentInfo('StartSyncSkippedForShuttingDownDb', {
    db: ctx.internalDatabaseIdentifier,
    phase,
    where,
  });
}

/**
 * True when this sync start must stop, for either of the two reasons a teardown
 * gives — THE ONE THING TO ASK AT AN AWAIT BOUNDARY. It asks both questions and
 * files the matching report, so a call site never has to pick between them.
 *
 * The two questions are different states of the same teardown, and both have to
 * be asked because neither implies the other at the moment it matters:
 *
 * - the handles have GONE (`databaseHandlesAreGone`). Dereferencing one now
 *   crashes, which is what this whole family of checks was written for.
 * - the shutdown has BEGUN while both handles are still set (`ctx.isShuttingDown`).
 *   `shutdownAsync` nulls the handles only after an unbounded wait for in-flight
 *   readers, so a wrapper spends that whole wait looking perfectly healthy to the
 *   check above. Nothing but the flag can see that window, and a replication
 *   started inside it is one nothing will ever cancel: the shutdown ran its single
 *   `cancelSync()` before the wait and never runs another.
 *
 * `where` names the point the pass had reached, so the call sites stay
 * distinguishable in Sentry. The caller decides what to do with a `true` — most
 * just return, and the site that has already created a replication cancels it.
 *
 * Why the window exists, why `cancelSync()` does not close it, and why both
 * reports must stay at the INFO tier: `design-docs/pass-through-db.md`,
 * "A sync start can outlive the handles it was given".
 */
function syncStartNoLongerWanted(
  ctx: SyncStartContext, phase: SyncPhase, logHeader: string, where: string,
): boolean {
  // the handles first: once they are gone that is the more specific answer, and
  // its report carries which of the two went missing
  if (databaseHandlesAreGone(ctx)) {
    reportSyncStartAbandoned(ctx, phase, where);
    return true;
  }
  if (ctx.isShuttingDown) {
    reportSyncStartSkippedForShutdown(ctx, phase, logHeader, where);
    return true;
  }
  return false;
}

/**
 * What one step of a sync start tells the function that runs the steps: carry
 * on to the next step, or stop here because the step has already finished the
 * start (a refusal, an abandoned start, or the empty-database short-cut).
 */
enum SyncStartStep {
  Continue,
  Stop,
}

/**
 * Start one replication run for this database.
 *
 * The caller (`DatabaseWrapper.startSync`) has already serialized this through
 * the sync mutex and decided which phase to run, so nothing here re-checks
 * either.
 *
 * This function only runs the steps, in order. Each step is a named function
 * below it. The checks for a shutdown that landed while a step was awaiting sit
 * HERE, between the steps, so an await added inside an existing step is already
 * covered by the check after that step, and a new awaiting step needs a check
 * after it. There is one thing to call at such a boundary —
 * `syncStartNoLongerWanted` — and it asks both liveness questions, so a new
 * checkpoint is one line and cannot get half of it wrong. The one check inside a
 * step is in the document-count step's catch, where the failure it is reasoning
 * about is the count throwing. See design-docs/pass-through-db.md, "A sync start
 * can outlive the handles it was given".
 */
export async function startSyncInternalAsync(ctx: SyncStartContext, phase: SyncPhase): Promise<void> {
  if (ctx.remoteOnly || ctx.temporaryDb) {
    ctx.publishSyncProgress({ database: ctx.databaseRef, state: 'end' });
    ctx.updateStateReady();
    // same reasoning as the early returns in assureInitialSyncAsync: no sync
    // runs here, so never leave boot waiting on the calendar gate
    ctx.whenCalendarWindowSyncCompleted.resolve(undefined);
    return;
  }

  // the two initial passes behave identically everywhere except the selector
  // cutoff and what happens when they finish, so most of this method still
  // reasons in terms of "is this an initial sync?"
  const initial = isInitialSyncPhase(phase);
  let logHeader = `Db:${ctx.internalDatabaseIdentifier}: [${initial ? `initial sync:${phase}` : 'live sync'}]`;

  if (checkSyncStartEntryGuards(ctx, phase, initial, logHeader) === SyncStartStep.Stop) {
    return;
  }

  console.log(`${logHeader} starting sync`);
  // a user database and every database an officer opens sync both ways; a
  // normal user only pulls a shared database. asked once, because the steps
  // below and the replication options both depend on the answer
  const syncsBothWays = ctx.isUserDb() || ctx.auth.getAuthRole() >= AuthRole.Officer;
  if (syncsBothWays) {

    // only on the FIRST initial pass: the singleton docs are already local by
    // the time the second pass runs, so repeating the existence check and the
    // doc_ids sync would add a remote round trip to every boot — the opposite
    // of what splitting the initial sync is for.
    if (phase === SyncPhase.CalendarWindow) {
      const singletonDocs = syncableSingletonIds(ctx);
      if (singletonDocs.length > 0) {
        const existence = await findExistingSingletonIdsAsync(ctx, singletonDocs);
        // the existence check above is awaited, so the wrapper may have been
        // shut down while it was outstanding. Nothing has been started yet, so
        // there is nothing to cancel on the way out.
        if (syncStartNoLongerWanted(ctx, phase, logHeader, 'after the singleton existence check')) {
          return;
        }
        startSingletonDocSync(ctx, existence.ids, logHeader, existence.answered);
      } else {
        ctx.setInitialSingletonSyncCompleted(true);
      }
    }

    if (initial && await checkInitialSyncDocCountsAsync(ctx, phase, logHeader) === SyncStartStep.Stop) {
      return;
    }
  }

  const syncFunction = buildSyncFunction(ctx, phase, initial, syncsBothWays);

  if (initial) {
    ctx.syncTasksCount = 1;
  } else {
    ctx.syncTasksCount = 0;
  }
  ctx.syncTasksCompleted = 0;
  // a new pass starts here, past every early return — so a pre-count still in
  // flight for the previous pass is now stale and must not install its answer.
  // the bump lives here rather than at the top of the method on purpose: an
  // early return ("sync already running") leaves the RUNNING pass's count
  // perfectly valid, and bumping there would throw it away
  ctx.syncGeneration += 1;
  ctx.initialSyncDocsToPull = null;
  ctx.initialSyncDocsReadBaseline = 0;
  ctx.lastPullDocsRead = 0;

  // the last gate before the handles are actually used. every await above ran
  // between the entry check and this line, and syncFunction reads both handles
  // when it is CALLED, not when it was defined — so this is the check that
  // stops the crash the others only narrow
  if (syncStartNoLongerWanted(ctx, phase, logHeader, 'before starting the replication')) {
    // the first initial pass may already have started the singleton pre-sync a
    // few lines above, and only `cancelSync` reaches that — see
    // design-docs/pass-through-db.md, "A sync start can outlive the handles it
    // was given", which says why nothing else does
    ctx.cancelSync();
    return;
  }

  const replication = syncFunction();
  ctx.syncHandler = replication;
  ctx.syncHandlerIsInitial = initial;

  // Last chance to catch a teardown, and the only window left is a narrow one:
  // the gate above asks the same two questions, and the three lines between it
  // and here do not await, so only `syncFunction()` itself can have changed the
  // answer. It is checked anyway because this is the point where the LEAK becomes
  // possible and the cost of being wrong is unbounded: `shutdownAsync` runs its
  // one `cancelSync()` BEFORE waiting, and never runs another, so a replication
  // assigned after that is one nothing else will ever stop. Cancel it here and the
  // wrapper goes back to holding no handler at all.
  if (syncStartNoLongerWanted(ctx, phase, logHeader, 'after-start')) {
    // Take the two error handlers BEFORE cancelling. Returning here skips the
    // handler chain `attachReplicationHandlers` installs, and that chain is the only thing
    // holding this replication's errors — so without these the object is left
    // with none at all, at the one moment an error is most likely: `shutdownAsync`
    // closes the remote handle immediately after this. What `localDb.sync(...)`
    // returns is a PouchDB `Sync`, and it guards neither route itself. It emits
    // `'error'` with whatever listeners exist, which is nothing on this path, and
    // its own source says that "would otherwise throw an unhandled error due to
    // 'error' being a special event in EventEmitters"; and unlike the `Replication`
    // the shared-database path uses, it installs no stub `.catch()` on the promise
    // behind it, so a rejection would surface as an unhandled one (#3987 A2,
    // defect D9, which is why the chain below carries a `.catch` at all).
    // Both handlers only log: the wrapper is being destroyed, so there is nothing
    // left to recover, and the refusal has already been reported above.
    replication
      .on('error', err => console.log(`${logHeader} the sync cancelled during shutdown reported an error (expected):`, err))
      .catch(err => console.log(`${logHeader} the sync cancelled during shutdown rejected (expected):`, err));
    ctx.cancelSync();
    return;
  }

  // started AFTER the replication so the download always goes first, and only
  // for the pass whose total the boot progress bar actually reads —
  // shouldPreCountInitialSyncDocs owns that rule and explains it.
  // fire-and-forget: it only ever improves the estimate
  if (shouldPreCountInitialSyncDocs(phase, ctx.isUserDb())) {
    void ctx.countInitialSyncDocsAsync(phase, logHeader);
  }

  attachReplicationHandlers(ctx, replication, phase, initial, logHeader);
}

/**
 * The checks that refuse a sync start before anything is awaited: a shutdown
 * already under way, a sync already running, or a database handle missing.
 *
 * This is the ONE place that still asks the two liveness questions separately
 * rather than through `syncStartNoLongerWanted`, and the reason is that their
 * ORDER relative to the other two checks is load-bearing in both directions:
 *
 * - the shutdown flag must be asked FIRST, before the duplicate-handler branch
 *   below, because that branch calls `cancelSync()` to supersede a running live
 *   sync — which is not something to do to a wrapper that is being torn down.
 * - the handles must be asked LAST, after the missing-remote check, or a null
 *   `remoteDb` on a healthy wrapper would be reported as an abandoned start
 *   instead of `StartSyncWithoutRemoteDb`, and that is the only event that would
 *   ever tell us such a caller exists.
 *
 * Collapsing them into one call would have to pick one of those orders and lose
 * the other, so they stay apart here on purpose.
 */
function checkSyncStartEntryGuards(
  ctx: SyncStartContext, phase: SyncPhase, initial: boolean, logHeader: string,
): SyncStartStep {
  // The wrapper is being torn down (sign-out, account switch, a superseded
  // start-up). `startSync` already refused once, but it did so before taking the
  // sync mutex, and a shutdown can begin while this call waits for it — so the
  // question is asked again here, on the near side of the replication. Checking
  // `remoteDb` below would not catch it: `shutdownAsync` nulls the handles only
  // after an unbounded wait for in-flight readers, so they are still set for the
  // whole of that wait. See design-docs/pass-through-db.md, "Which databases a
  // sweep may act on".
  if (ctx.isShuttingDown) {
    reportSyncStartSkippedForShutdown(ctx, phase, logHeader, 'before-start');
    return SyncStartStep.Stop;
  }

  // syncMutex serializes startSync, so reaching here with a live handler is the
  // expected coalescing of near-simultaneous online-state + app-resume triggers:
  // the second caller's intent (ensure a sync is running) is already satisfied.
  // Quiet no-op — NOT a tracked silent problem (was StartSyncDuplicateHandler,
  // #3493 Phase A; the race it flagged is now prevented by the mutex).
  if (ctx.syncHandler != null) {
    // if a needed initial sync (see shouldRestartWithInitialSync) finds a *live* sync already
    // running, supersede it: cancel + run initial, else the initial would no-op forever and a
    // shared db's reads stay stranded (#3493 Phase A). Never cancel a genuine in-flight initial.
    if (initial && ctx.initialSyncCompleted !== true && !ctx.syncHandlerIsInitial) {
      console.log(`${logHeader} initial sync needed — superseding running live sync`);
      ctx.cancelSync();
    } else {
      console.log(`${logHeader} sync already running, skipping duplicate start (initial=${initial})`);
      return SyncStartStep.Stop;
    }
  }
  // if a restore or purge detached the handle on purpose, this start is a late one from the
  // remote's own initialization (it awaits before calling startSync), and stopping it is right
  if (ctx.remoteDb == null && ctx.remoteDetachedForOwnWork > 0) {
    console.log(`${logHeader} not starting sync — the remote database is detached for a restore or purge`);
    ctx.logging.trackSilentInfo('StartSyncSkippedForOwnRemoteDetach', { db: ctx.internalDatabaseIdentifier, initial });
    return SyncStartStep.Stop;
  }
  if (ctx.remoteDb == null) {
    ctx.logging.trackSilentProblem('StartSyncWithoutRemoteDb', {
      db: ctx.internalDatabaseIdentifier,
      initial,
      hasCouchAuth: ctx.auth.couch != null,
      // eslint-disable-next-line no-restricted-syntax -- diagnostic field: raw navigator.onLine is itself the signal being recorded (F9)
      online: navigator.onLine,
    });
    return SyncStartStep.Stop;
  }
  // the remote handle is present, so a missing LOCAL one is a state we have
  // never observed at this point — every non-remote-only wrapper creates its
  // local store before it creates the remote one, and each teardown path clears
  // both together. Checked anyway: reaching the dereferences below with it null
  // crashes, and this event would be the only clue that such a caller exists.
  if (databaseHandlesAreGone(ctx)) {
    reportSyncStartAbandoned(ctx, phase, 'before starting');
    return SyncStartStep.Stop;
  }
  return SyncStartStep.Continue;
}

/** the singleton documents the first initial pass syncs ahead of everything else */
function syncableSingletonIds(ctx: SyncStartContext): string[] {
  // device-local singletons (the day-task-status cache) never sync — each
  // device rebuilds its own copy (#4212)
  return Object.keys(ctx.singletons).filter(id => !ctx.deviceLocalDocIds.includes(id));
}

/**
 * Ask the remote database which of the singleton documents exist. The answer is
 * awaited, so the caller must check for a shutdown after it.
 */
async function findExistingSingletonIdsAsync(
  ctx: SyncStartContext, singletonDocs: string[],
): Promise<{ ids: string[]; answered: boolean }> {
  // bounded by SINGLETON_EXISTS_TIMEOUT_MS — a hung allDocs here would
  // wedge boot before the syncHandler is assigned (#3898). On timeout
  // the helper returns empty ids, so we fall through to the main initial
  // sync below (its selector already covers singleton docs).
  const singletonExists = await resolveExistingSingletonIds(
    () => ctx.remoteDb.allDocs({ keys: singletonDocs, include_docs: false }),
    SINGLETON_EXISTS_TIMEOUT_MS,
  );
  // if the existence check timed out or failed, surface it — this is the
  // signal that the #3898 boot-wedge guard engaged (and that the boot
  // connection was half-open), which would otherwise be invisible.
  //
  // #6096: except when the phone froze the app mid-check. The limit still gives up the
  // moment it wakes, which is right, but that is not a hang: report it as info. The start
  // carries on into the main initial sync, whose own failures are recorded, so a freeze
  // followed by a real hang still surfaces there.
  //
  // #6120: and except an ordinary dropped request (no signal, app in the background, DNS).
  // The main initial sync fetches the same documents and recovers from that on its own, and
  // the Sentry transient filter cannot see a trackSilentProblem event, so skip it here.
  const afterFreeze = timeoutCausedByFreeze(singletonExists.abandonedError);
  if (afterFreeze) {
    ctx.logging.trackSilentInfo('SingletonExistsCheckAbandonedAfterFreeze', {
      db: ctx.internalDatabaseIdentifier,
      not_running_ms: afterFreeze.notRunningMs,
    });
  } else if (isTransientFetchError(singletonExists.abandonedError)) {
    console.log(`Db:${ctx.internalDatabaseIdentifier}: singleton existence check dropped by the network — not reported, the main initial sync fetches the singletons`,
      ctx.serializeSyncError(singletonExists.abandonedError));
  } else if (singletonExists.abandonedError) {
    ctx.logging.trackSilentProblem('SingletonExistsCheckAbandoned', {
      db: ctx.internalDatabaseIdentifier,
      error: ctx.serializeSyncError(singletonExists.abandonedError),
    });
  }
  // `answered` false: the check was abandoned, so an empty list says nothing about what the
  // server holds (#6248)
  return { ids: singletonExists.ids, answered: singletonExists.abandonedError == null };
}

/** which of the singleton sync's failure handlers recorded a failure */
type SingletonSyncFailureSource = 'singleton_error' | 'singleton_denied' | 'singleton_catch';

/**
 * The bookkeeping every singleton sync failure handler shares: hand an IndexedDB
 * connection error over to recovery, otherwise remember the error and mark both
 * initial syncs as not completed so assureInitialSyncAsync retries. Each handler
 * keeps its own log line; this is only the part that must stay identical.
 */
function recordSingletonSyncFailure(ctx: SyncStartContext, source: SingletonSyncFailureSource, err: any): void {
  // if this is an IndexedDB connection error, attempt recovery (reload)
  if (ctx.databaseRecovery.interceptDatabaseError(err, 'singleton-pre-sync')) {
    return;
  }
  ctx.lastSyncError = { source, error: ctx.serializeSyncError(err), timestamp: DateTime.utc().toISO() };
  // #6554: a failed round, not a verdict — reads stay on the server while online
  ctx.recordInitialSyncRoundFailed();
  ctx.setInitialSingletonSyncCompleted(false);
}

/** Stop holding the singleton pre-sync, now that this replication has ended.
 *
 * **Only the replication currently held is released.** A cancelled replication's ending
 * arrives asynchronously, and `cancelSync` is followed straight away by a fresh start on the
 * supersede path — so by the time an old ending runs, a NEWER singleton pre-sync can already
 * be published. Clearing the field unconditionally would un-publish that newer one and leave
 * it uncancellable, which is the very state this whole change removes.
 *
 * Holding a replication that has already ended would make a later `cancelSync` call into
 * PouchDB for nothing, and would keep the object alive for the rest of the wrapper's life. */
function releaseSingletonDocSync(ctx: SyncStartContext, replication: any): void {
  if (ctx.singletonSyncHandler === replication) {
    ctx.singletonSyncHandler = null;
  }
}

/** Stop holding the main replication, now that a failed initial pass has ended it
 * (df-71af65f8fc1e).
 *
 * Only `cancelSync` cleared `syncHandler`, and the retry loop is the only caller that cancels
 * a failed initial pass. An initial pass started outside it (`ensureSyncActive` after the loop
 * gave up while online, or `initializeRemoteDb` with the remote already attached) stayed held
 * after its error, with `syncHandlerIsInitial` still true, so `ensureSyncActive` took it for an
 * initial sync in progress and `startSync(true)` refused as "already running" for the rest of
 * the session.
 *
 * Only the replication still held is released, for the same reason as
 * `releaseSingletonDocSync`: a newer one started after a `cancelSync` must stay reachable. */
function releaseFailedInitialSync(ctx: SyncStartContext, replication: any): void {
  if (ctx.syncHandler === replication) {
    ctx.syncHandler = null;
    ctx.syncHandlerIsInitial = false;
  }
}

/** start the small doc_ids sync of the singleton documents that exist remotely
 *
 * **The replication is published on the context, and that is load-bearing (#5306).** This is a
 * SECOND replication on the same two handles, separate from the one `syncHandler` holds. While
 * it was stored nowhere, nothing could cancel it: `cancelSync` cancels `syncHandler` and
 * nothing else, and `shutdownAsync` calls only `cancelSync`. So a wrapper superseded by a
 * second app start-up shut down with this replication still running, `remoteDb.close()` landed
 * under it, and its next remote call came back with PouchDB's `database is closed` — reported
 * as `DatabaseClosedHandleCalled` with nothing in it that could name the caller. See
 * `design-docs/boot-sequence.md`, "A call on a handle we closed ourselves is not a broken
 * IndexedDB".
 *
 * **What is published is the sync object, captured BEFORE any handler is attached** — the same
 * shape `startSyncInternalAsync` uses for `syncHandler`. It has to be: `.on()` hands the sync
 * object back, but PouchDB's `Sync.catch` is `return promise.catch(err)`, so the value of a
 * chain ending in `.catch` is a plain Promise with no `cancel` method. Publishing that would
 * make `cancelSync` throw, and `cancelSync` is the first real step of `shutdownAsync` — so the
 * shutdown would stop before it closed anything. */
function startSingletonDocSync(ctx: SyncStartContext, existingDocs: string[], logHeader: string, existenceAnswered: boolean): void {
  if (existingDocs.length > 0) {
    console.log(`${logHeader} Syncing singleton docs:`, existingDocs);
    const singletonSync = ctx.localDb.sync(ctx.remoteDb, {
      doc_ids: existingDocs,
      style: 'main_only',
      checkpoint: 'source',
      pull: { checkpoint: 'target' },
    });
    ctx.singletonSyncHandler = singletonSync;
    singletonSync.on('complete', (info: any) => {
      console.log(`${logHeader} end syncing singleton docs:`);
      releaseSingletonDocSync(ctx, singletonSync);
      // #5272's rule, which `cancelSync` cancelling this sync makes an ordinary outcome
      // here rather than a rare one: PouchDB reports a CANCELLED replication through this
      // same event, and only the status tells the two apart. Saying the singleton pre-sync
      // finished when a teardown stopped it resolves a wait that nothing actually satisfied.
      // NOT only reachable via `cancelSync` — PouchDB cancels a direction itself when the
      // local store is destroyed under it. Safe to read the status here for the same reason
      // it is safe there: this pass is not `live`. See design-docs/sync-integrity.md,
      // "A Cancelled Replication Is Not a Finished One".
      const wasCancelled = (outcome: { status?: string } | undefined) => outcome?.status === 'cancelled';
      if (wasCancelled(info) || wasCancelled(info?.pull) || wasCancelled(info?.push)) {
        console.log(`${logHeader} the singleton pre-sync was cancelled, so it did NOT complete`);
        return;
      }
      ctx.setInitialSingletonSyncCompleted(true);
    }).on('paused', (info: any) => {
      console.log(`${logHeader} paused syncing singleton docs:`, info);
    }).on('active', (info: any) => {
      console.log(`${logHeader} active syncing singleton docs:`, info);
    }).on('error', (err: any) => {
      // log as info — assureInitialSyncAsync has app-level retry logic
      console.log(`${logHeader} error syncing singleton docs (will retry):`, err);
      // terminal: PouchDB calls its own `cancel()` before emitting this
      releaseSingletonDocSync(ctx, singletonSync);
      recordSingletonSyncFailure(ctx, 'singleton_error', err);
    }).on('denied', (err: any) => {
      // log as info — assureInitialSyncAsync has app-level retry logic
      console.log(`${logHeader} denied syncing singleton docs (will retry):`, err);
      // NOT terminal, so the handle is deliberately NOT released here. PouchDB emits
      // `denied` once per document the server refused and carries on replicating the
      // rest; releasing here would un-publish a replication that is still running, which
      // is exactly the uncancellable state this change exists to remove.
      recordSingletonSyncFailure(ctx, 'singleton_denied', err);
    }).catch((err: any) => {
      // terminal: the sync object's promise only settles once the replication is over
      releaseSingletonDocSync(ctx, singletonSync);
      // PouchDB internal replication can produce 409 conflicts when singleton docs
      // are written locally (by a singleton save during start-up) while sync pulls remote versions.
      // These are harmless — PouchDB resolves them internally. Catch the promise
      // rejection to prevent unhandled rejection noise in Sentry (3MH).
      if (isPouchConflict(err)) {
        console.log(`${logHeader} Ignoring 409 during singleton sync (expected race with local init)`);
        return;
      }
      // non-409 errors are already handled by .on('error') EventEmitter path,
      // but catch here too as a safety net
      console.log(`${logHeader} Singleton sync promise rejected (will retry):`, err);
      recordSingletonSyncFailure(ctx, 'singleton_catch', err);
    });

    console.log(`${logHeader} end syncing singleton docs:`);
  } else {
    // nothing to sync. If the existence check was abandoned, "nothing" is not an answer, so the
    // start-up's dropped-read fallback must not read a missing document as "not on the server"
    // (#6248). The boot itself carries on unchanged: the main initial sync covers singletons.
    ctx.setInitialSingletonSyncCompleted(true, existenceAnswered);
  }
}

/**
 * Compare the remote and local document counts before an initial pass. Stops the
 * start when the remote database is empty (and hands over to live sync), when a
 * dead IndexedDB connection was handed to recovery, or when a shutdown made a
 * count throw.
 */
async function checkInitialSyncDocCountsAsync(
  ctx: SyncStartContext, phase: SyncPhase, logHeader: string,
): Promise<SyncStartStep> {
  try {
    let remoteDbInfo = await timeout(10_000, ctx.remoteDb.info() as Promise<{ doc_count: number }>);
    let localDbInfo = await ctx.localDb.info();
    console.log(`${logHeader} Remote database doc count: ${remoteDbInfo.doc_count}; local database doc count: ${localDbInfo.doc_count}`);
    // if remote has significantly more docs than local, signal a large first-time download
    const docsToSync = remoteDbInfo.doc_count - localDbInfo.doc_count;
    if (docsToSync > 100) {
      ctx.publishSyncProgress({ largeFirstSync: true });
    }
    if (remoteDbInfo.doc_count === 0) {
      console.log(`${logHeader} Remote database is empty, skipping sync.`);
      ctx.initialSyncSkippedEmpty = true;
      ctx.setInitialSyncCompleted(true);
      ctx.updateStateSyncing();
      void ctx.startSync(false);
      return SyncStartStep.Stop;
    }
  } catch (err) {
    // if IDB connection is dead, trigger recovery — sync will also fail
    if (ctx.databaseRecovery.interceptDatabaseError(err, 'sync-doc-count-check')) {
      return SyncStartStep.Stop;
    }
    // a teardown under either info() call throws here too, and there is
    // nothing left to proceed WITH — say so instead of announcing a sync
    // that the caller's check before the replication is about to abandon anyway
    if (syncStartNoLongerWanted(ctx, phase, logHeader, 'during the document-count check')) {
      // the singleton pre-sync may already be running, and only `cancelSync`
      // stops it — same reasoning as the gate before the replication, which the
      // caller reaches by another route. Deliberately NOT done for the two
      // non-teardown stops above: an empty remote hands over to live sync, and a
      // dead IndexedDB connection has gone to recovery.
      ctx.cancelSync();
      return SyncStartStep.Stop;
    }
    // info() failed or timed out — can't check doc_count, proceed with sync
    console.log(`${logHeader} remoteDb.info() failed, proceeding with sync:`, err);
  }
  return SyncStartStep.Continue;
}

/**
 * Build the function that creates this pass's replication. It reads both
 * database handles when it is CALLED, not here — which is why the caller checks
 * for a shutdown immediately before calling it.
 */
function buildSyncFunction(
  ctx: SyncStartContext, phase: SyncPhase, initial: boolean, syncsBothWays: boolean,
): () => any {
  if (syncsBothWays) {
    if (initial) {
      return () => {
        const selector = {
          $and: [
            // Exclude server-side design docs and device-local docs (#4212).
            // Taken from the shared helper, not spelled out again here: the
            // progress-bar pre-count skips exactly this list too, and two
            // hand-written copies would let the count drift from the sync.
            { _id: { $nin: [...ctx.syncExcludedDocIds()] } },
            {
              $or: [
                { _deleted: { $exists: false } },  // Matches when `_deleted` is missing (normal docs)
                { _deleted: false }  // Matches explicitly `_deleted: false`
              ]
            },
            // priority sync: all non-log docs + recent/future log entries only.
            // Older log entries sync in the background via live sync (separate
            // checkpoint). The cutoff depends on which initial pass this is:
            // the first covers only the task calendar's window so its dots fill
            // in early, the second widens to the full priority window.
            //
            // THIS SELECTOR IS THE AUTHORITY on which documents an initial
            // pass moves, and `buildInitialSyncIdRanges` (in
            // initial-sync-progress.ts) re-describes the same id space as
            // allDocs key ranges so the progress bar can count them. The two
            // must be edited together — if they disagree, the bar divides by
            // the wrong number. The excluded-id list above is already shared
            // through `syncExcludedDocIds()`, so the id ranges below are the
            // only part of the selector with a second copy anywhere.
            {
              $or: [
                { _id: { $lt: 'log/' } },                                                // all docs before log/ namespace
                { _id: { $gte: 'log/\ufff0' } },                                          // all docs after log/ namespace
                { _id: { $gte: `log/${phase === SyncPhase.CalendarWindow
                  ? ctx.getCalendarSyncCutoffDate()
                  : ctx.getPrioritySyncCutoffDate()}` } },                   // recent + future logs
              ]
            }
          ]
        };
        // an officer's global_common push leaves the food documents out (#5679 — see
        // GLOBAL_COMMON_UPLOAD_ONLY_ID_PREFIX). It has to be a selector: PouchDB refuses a
        // function filter beside one. Built from the pull's selector so the two cannot drift.
        const pushSelector = ctx.isGlobalCommonDb()
          ? { $and: [...selector.$and, GLOBAL_COMMON_PUSHABLE_ID_CLAUSE] }
          : undefined;
        return ctx.localDb.sync(ctx.remoteDb, {
          live: false,
          retry: false,
          checkpoint: 'source',
          pull: { checkpoint: 'target' },
          ...(pushSelector ? { push: { selector: pushSelector } } : {}),
          batch_size: 200,
          selector,
          style: 'main_only',
        });
      }
    } else {
      return () => {
        const push = {
          // The push direction needs its OWN changes-feed style — see
          // `DatabaseWrapper.USER_DB_PUSH_CHANGES_STYLE` for why. PouchDB merges
          // a per-direction object over the top-level options, so the pull below
          // keeps `main_only`.
          //
          // Only for the USER database. This same two-way sync also runs for
          // an officer's shared databases (see the branch condition above),
          // and those must keep the old behaviour: what makes offering every
          // branch tip safe is that something closes the ones nobody wants,
          // and the conflict resolver only ever sweeps the user database. An
          // officer's device would otherwise upload losing branches on
          // curated shared documents that nothing would ever close.
          ...(ctx.isUserDb() ? { style: ctx.userDbPushChangesStyle } : {}),
          // An officer's global_common push leaves the food documents out (#5679 — see
          // GLOBAL_COMMON_UPLOAD_ONLY_ID_PREFIX). A per-direction filter REPLACES the
          // top-level one for that direction, so it has to ask replicationFilter itself.
          ...(ctx.isGlobalCommonDb()
            ? { filter: (doc: PouchDoc) => ctx.replicationFilter(doc) && isPushableToGlobalCommon(doc._id) }
            : {}),
        };
        return ctx.localDb.sync(ctx.remoteDb, {
          live: true,
          retry: true,
          checkpoint: 'source',
          pull: { checkpoint: 'target' },
          ...(Object.keys(push).length > 0 ? { push } : {}),
          filter: doc => ctx.replicationFilter(doc),
          style: 'main_only',
        });
      }
    }

  } else {
    // A normal user pulls a shared database read-only (checkpoint on target,
    // since we can't write to source). For `global_common` (#4284) the JS
    // replicationFilter is replaced by a server-side Mango selector that keeps
    // only English + the active language of `$text/`, so the other languages
    // never reach the device. The selector already carries the same design-doc /
    // device-local exclusions replicationFilter does, so the two must NOT both
    // be passed (a `selector` is realized as `filter: '_selector'` internally).
    // The one selector covers both initial and live sync, so they can't drift.
    // Every other shared db keeps the JS filter.
    const localeSelector: MangoSelector | undefined = ctx.isGlobalCommonDb()
      ? buildGlobalCommonLocaleSelector(ACTIVE_TEXT_SEGMENT, ctx.syncExcludedDocIds())
      : undefined;
    return () => {
      return ctx.localDb.replicate.from(ctx.remoteDb, {
        live: !initial,
        retry: !initial,
        checkpoint: 'target',
        ...(localeSelector
          ? { selector: localeSelector }
          : { filter: (doc: PouchDoc) => ctx.replicationFilter(doc) }),
        style: 'main_only',
      });
    }
  }
}

/** the handlers that run for the life of one replication, including how an initial pass ends */
function attachReplicationHandlers(
  ctx: SyncStartContext, replication: any, phase: SyncPhase, initial: boolean, logHeader: string,
): void {
  let alreadyEnded = false;
  let endInitialSync = async (opts: { complete: boolean }) => {
    console.log(`${logHeader} Initial sync ${opts.complete ? 'completed' : 'TIMED OUT'}`);
    if (alreadyEnded) {
      console.log(`${logHeader} Already ended, skipping...`);
      return;
    }
    alreadyEnded = true;

    ctx.syncTasksCompleted += 1;

    // The first initial pass only covered the task calendar's window. Advance to
    // the second pass instead of finishing: the months behind the calendar have
    // NOT arrived yet, so declaring the initial sync complete here would let the
    // task-status cache persist `no_task` over real training days (#4212). Keep
    // the progress event as 'start' so the indicator stays up across both passes
    // rather than appearing to finish twice.
    // Only the user database has a meaningful split: the phase changes nothing
    // but the log cutoff in ITS selector, so for a shared database the two
    // passes are byte-identical and advancing would just re-run a replication
    // that already completed — an extra remote round trip per shared database
    // on every cold boot.
    if (phase === SyncPhase.CalendarWindow && ctx.isUserDb()) {
      ctx.cancelSync(); // have to remove the scheduled one
      // the calendar now has real data — release the boot screen before the
      // second (longer) pass runs
      ctx.whenCalendarWindowSyncCompleted.resolve(opts.complete);
      if (ctx.remoteDb) {
        console.log(`${logHeader} calendar window synced — widening to the full priority window`);
        void ctx.startSync(SyncPhase.PriorityWindow);
      }
      return;
    }

    ctx.publishSyncProgress({ database: ctx.databaseRef, state: 'end' });
    ctx.cancelSync(); // have to remove the scheduled one
    if (ctx.remoteDb) {
      ctx.setInitialSyncCompleted(true);
      console.log(`${logHeader} Initial sync completed`);
      // clear stale change tracking from initial sync and seed live sync time —
      // the changes feed fires on sync-pulled local writes, setting lastUserDbChangeTime_utc.
      // seed lastUserDbSyncTime_utc so the first live sync pause doesn't fire a false
      // silent-stall Sentry signal (#3274 — flags now drive that diagnostic, not UI offline state)
      if (ctx.isUserDb()) {
        delete sessionStorage.lastUserDbChangeTime_utc;
        sessionStorage.lastUserDbSyncTime_utc = DateTime.utc().toISO();
        // #4010 B3: mirror the seed — initial sync completing means nothing is pending,
        // so the cross-session check must not see a stale pre-sync change marker
        localStorage.removeItem('cs_lastUserDbChangeTime_utc');
        localStorage.cs_lastUserDbSyncTime_utc = sessionStorage.lastUserDbSyncTime_utc;
      }
      ctx.updateStateSyncing();
      void ctx.startSync(false);
      // fresh sync session — the listener starts clean, so its bounded
      // auto-restart budget resets too (#3987 A2)
      ctx.realtimeListenerRestarts = 0;
      ctx.startRealTimeListener();
    }
  }

  replication
    .on('complete', info => {
      ctx.flushSyncDiagnostics();

      let describe = (p: { status: string, ok: boolean } | undefined) => typeof p === 'object' ? `${p.status}/${p.ok ? 'ok' : 'not-ok'}` : 'n/a';
      console.log(`${logHeader} complete, pull: ${describe(info.pull)}, push: ${describe(info.push)}.`);
      if (initial) {
        // #5272: PouchDB reports a CANCELLED replication through this same event,
        // and only the status tells the two apart. Treating a cancelled run as a
        // finished one declares the initial sync successful on a wrapper being
        // torn down. The status sits in a different place for each of the two
        // call shapes, which is why all three are checked.
        //
        // Reading the status is safe ONLY because an initial pass runs with
        // `live: false`. Changing that breaks this check — see
        // design-docs/sync-integrity.md, "A Cancelled Replication Is Not a
        // Finished One", which also says why the status is read rather than the
        // replication object's own cancelled flag.
        const wasCancelled = (outcome: { status?: string } | undefined) => outcome?.status === 'cancelled';
        if (wasCancelled(info) || wasCancelled(info.pull) || wasCancelled(info.push)) {
          console.log(`${logHeader} the replication was cancelled, so this is NOT an initial sync completion`);
          return;
        }
        void endInitialSync({ complete: true });
      }

    })
    .on('active', info => {
      console.log(`${logHeader} Sync active`, info);
      ctx.lastSyncActivityTime_ms = DateTime.utc().toMillis();
      ctx.updateStateSyncing();
    })
    .on('change', info => {
      ctx.lastSyncActivityTime_ms = DateTime.utc().toMillis();
      const change = info.change || info;
      const docsRead = change.docs_read ?? 0;
      const docsWritten = change.docs_written ?? 0;
      const pending = change.pending ?? 0;
      const firstDocId = change.docs?.[0]?._id ?? '?';
      console.log(`${logHeader} Updated: ${info.direction || 'sync'} read=${docsRead} written=${docsWritten} pending=${pending} first=${firstDocId}`);
      let shouldUpdateState = false;
      // once the pre-count has answered, the total is a real document count and
      // a batch is worth reporting even when `pending` is 0 or absent — that is
      // exactly the tail of the download, the part the old estimate raced
      // through. before it answers, keep the old "only report when pending is
      // truthy" behaviour, since without pending there is nothing to divide by
      const knownDocsToPull = ctx.initialSyncDocsToPull;
      const publishPullProgress = (docsDone: number, docsPending: number) => {
        const counters = computePullSyncTaskCounters({
          docsDone,
          docsPending,
          knownDocsToPull,
          docsDoneBaseline: ctx.initialSyncDocsReadBaseline,
        });
        ctx.syncTasksCount = counters.syncTasksCount;
        ctx.syncTasksCompleted = counters.syncTasksCompleted;
        ctx.publishSyncProgress({ database: ctx.databaseRef, state: 'start' });
      };
      if (info.direction) {
        if (info.direction === 'pull') {
          // record the running total for the pre-count's baseline, whether or
          // not this batch is worth publishing
          ctx.lastPullDocsRead = info.change.docs_read as number;
          if (info.change.pending || knownDocsToPull != null) {
            publishPullProgress(info.change.docs_read as number, (info.change.pending as number) ?? 0);
          }
        }
        // a real pull total owns the counters — a push batch must not overwrite
        // them with its own (much smaller) denominator and snap the bar to full.
        // a total of zero is the exception: there is nothing to pull, so the
        // push is the only work there is and it should drive the bar. that
        // happens on the recovery and restore paths, where startSync(true) runs
        // against an already-populated local database
        const pullTotalOwnsTheBar = knownDocsToPull != null && knownDocsToPull > 0;
        if (info.direction === 'push' && info.change.pending && !pullTotalOwnsTheBar) {
          const docsWritten = info.change.docs_written as number;
          const docsPending = info.change.pending as number;
          ctx.syncTasksCount = docsWritten + docsPending + 1;
          ctx.syncTasksCompleted = docsWritten;
          ctx.publishSyncProgress({ database: ctx.databaseRef, state: 'start' });
        }
      } else {
        // one-way replicate.from — only the shared databases take this path, and
        // they have no pre-count, so `pending` is the only total available
        if (info.pending) {
          publishPullProgress(info.docs_read as number, info.pending as number);
        }
      }

      if (ctx.isUserDb()) {
        if (!initial && info.direction) {
          let deviceId = ctx.getDeviceId();
          // collect a bounded sample of ids (plus a total count) of docs pulled
          // from another device, so we can log one string-only summary line
          // after the loop instead of the whole document per doc — the per-doc
          // log froze cold starts (#4243). only the sample is held, so a large
          // batch never keeps one string per pulled doc in memory.
          const pulledFromOtherDeviceSample: string[] = [];
          let pulledFromOtherDeviceCount = 0;
          // #4249: docs this loop decides are real remote changes, handed to
          // the pulled-doc listeners once after the loop. Only filled when a
          // listener is actually registered, so the common case costs nothing.
          const pulledDocsForListeners: any[] | null = ctx.pulledDocListenerCount > 0 ? [] : null;
          // #4441 / #4464: pulled tombstones — set aside in the loop, settled
          // after it. Allocated lazily, so a batch without a deletion costs
          // nothing. Unlike the array above this is NOT gated on a registered
          // listener: the identity-map eviction and the #4464 repair have to
          // happen either way.
          let pulledDeletionsToSettle: PulledDeletionToSettle[] | null = null;
          for (const doc of info.change.docs) {
            const transactionParts = (doc.db__trans && doc.db__trans.split('|')) || doc;
            const transactionDeviceId = (transactionParts && transactionParts[1]) || null;

            // Update expected _rev when sync pulls changes (NOT on push - push confirmations
            // may be for older saves while we've already saved a newer revision locally)
            // Only update if pulled rev is newer to avoid race conditions with local saves
            if (info.direction === 'pull' && doc._rev && !doc._deleted) {
              // the identity-map rule itself lives in applyPulledDocToCache and
              // is shared with the two other channels a pulled doc can arrive
              // through, with no exception for this path: a doc we have never
              // cached is announced whichever device wrote it (#5189).
              const applied = ctx.applyPulledDocToCache(doc);
              // if same device — likely multi-tab (tabs share localStorage.deviceId but have
              // separate in-memory caches) or cross-session (old db__trans from a previous session).
              // Logged for diagnostics only, not reported to Sentry.
              const isUnexpectedSameDeviceRev = applied.wasCached
                && applied.generationStrictlyNewer
                && transactionDeviceId === deviceId
                && !document.hidden;
              if (isUnexpectedSameDeviceRev) {
                console.log(`[SYNC] Pulled newer rev from same device: ${doc._id} pulled=${doc._rev} expected=${applied.previousExpectedRev}`);
                ctx.unexpectedSyncRevs.push({
                  docId: doc._id,
                  pulledRev: doc._rev,
                  expectedRev: applied.previousExpectedRev!,
                  isOwnSession: true,
                });
              }
              // one delivery for the whole batch after the loop, never per doc
              // (#4243) — and nothing is collected at all when no listener is
              // registered, which is what the null above means.
              if (applied.undeliveredListenerDoc) {
                pulledDocsForListeners?.push(applied.undeliveredListenerDoc);
              }
            }
            // notify watchers for pulled deletions so UI can react
            // (e.g. coach-settings re-queries slot list when server deletes a slot)
            if (info.direction === 'pull' && doc._deleted) {
              // #4249: read the cached body BEFORE the eviction below. A pulled
              // tombstone is only {_id, _rev, _deleted}, so this is the last
              // chance to learn what was deleted and when.
              const bodyBeforeEvict = ctx.documentCache.get(doc._id)?.ref.deref();
              // #4314: a conflict close deletes ONE losing revision, not the
              // document — when its winner is still alive, evicting here would
              // strand a page holding the object, and publishing would make
              // the reactive path recompute a calendar day that did not
              // change.
              //
              // #4441: the flag alone cannot decide that. It says "THIS
              // REVISION closed a branch", not "the DOCUMENT still has a
              // living version". So the local database is asked, after the
              // loop. #4464: that same answer decides a second thing, for
              // EVERY pulled tombstone rather than only the flagged ones —
              // whether this device is holding a branch the server has already
              // closed. The settle helper's header explains both.
              //
              // An ordinary deletion is unambiguous and still evicts and
              // publishes right here, costing no database read (#4243); it is
              // collected only so the second question gets asked about it, and
              // `alreadyHandledInLoop` tells the settle step not to redo this.
              const alreadyHandledInLoop = !(doc as any).db__conflictClose;
              (pulledDeletionsToSettle ??= []).push({
                docId: doc._id,
                cachedBody: bodyBeforeEvict ?? null,
                tombstone: doc,
                alreadyHandledInLoop,
              });
              if (alreadyHandledInLoop) {
                ctx.documentCache.delete(doc._id);
                if (pulledDocsForListeners != null) {
                  pulledDocsForListeners.push(bodyBeforeEvict != null ? { ...bodyBeforeEvict, _deleted: true } : doc);
                }
              }
              ctx.notifyDocWatchers(doc._id);
            }
            if (info.direction === 'pull' && transactionDeviceId && transactionDeviceId !== deviceId && !(doc._id.startsWith('mix')) && !doc._deleted) {
              pulledFromOtherDeviceCount++;
              if (pulledFromOtherDeviceSample.length < PULLED_FROM_OTHER_DEVICE_LOG_SAMPLE_SIZE) {
                pulledFromOtherDeviceSample.push(doc._id);
              }
            }
            shouldUpdateState = true;
          }
          // one string-only summary for the whole batch — never a document
          // body (#4243). formatPulledFromOtherDeviceLog returns null when
          // nothing was pulled from another device, so we log nothing then.
          const otherDeviceSummary = formatPulledFromOtherDeviceLog(pulledFromOtherDeviceSample, pulledFromOtherDeviceCount, deviceId);
          if (otherDeviceSummary) {
            console.log(`${logHeader} ${otherDeviceSummary}`);
          }
          // #4249: one delivery for the whole batch, after the loop
          if (pulledDocsForListeners != null) {
            ctx.deliverPulledDocsToListeners(pulledDocsForListeners);
          }
          // #4441 / #4464: and then settle the pulled tombstones, which need a
          // database read to tell a branch close from a real deletion, and to spot a
          // document this device is still holding alive after the server deleted it.
          // Fire-and-forget on purpose: this handler must stay synchronous so
          // one pull batch is never interleaved with the next.
          if (pulledDeletionsToSettle != null) {
            void ctx.settlePulledDeletionsAsync(pulledDeletionsToSettle);
          }
        } else if (initial && info.direction === 'pull' && ctx.pulledDocListenerCount > 0) {
          // #4249 Phase 2 (P1): the branch above is gated on `!initial`, so
          // during the catch-up sync nothing iterates the arriving documents
          // at all — which is why calendar dots used to freeze until the whole
          // sync finished. This is the minimal counterpart for that window.
          //
          // Deliberately NOT the loop above. That one maintains the identity
          // map, compares revisions and notifies document watchers per doc.
          // Doing all of that for every document of a 16,000-document cold
          // start is the work that saturated the main thread in #4243. Here we
          // only ever look at the id and, for a matching document, copy six
          // primitive fields.
          //
          // Nothing retains a replication document: those carry `_revisions`
          // (50+ hashes on a conflict-heavy doc), and holding a batch of them
          // is the other half of the #4243 memory story. Each entry below is a
          // fresh small object of strings, numbers and one boolean.
          const pulledLogSummaries: any[] = [];
          for (const doc of info.change.docs) {
            // if this is not a log entry, no calendar day can depend on it
            if (!doc?._id?.startsWith('log/')) continue;
            pulledLogSummaries.push({
              _id: doc._id,
              type: doc.type,
              time_utc: doc.time_utc,
              timeOffset_min: doc.timeOffset_min,
              state: doc.state,
              _deleted: doc._deleted,
            });
          }
          // one delivery per batch, never per document — same discipline as
          // the live path above
          if (pulledLogSummaries.length > 0) {
            ctx.deliverPulledDocsToListeners(pulledLogSummaries);
          }
        }
        sessionStorage.lastUserDbSyncTime_utc = DateTime.utc().toISO();
        // #4010 B3: mirror the ack for the cross-session unflushed check (this whole block
        // is already user-db-only via the enclosing guard, same as the sessionStorage write)
        localStorage.cs_lastUserDbSyncTime_utc = sessionStorage.lastUserDbSyncTime_utc;
        // df-ed6d17c8ec6f: record the last time a PUSH batch actually reached the server.
        // Only a push proves our own writes are landing; the sync flag above is bumped by a
        // PULL too, which is exactly what masks a persistently-failing push. The paused
        // handler's reportPushStallIfNeeded reads this to tell a stuck push from a healthy one.
        if (info.direction === 'push') {
          sessionStorage.lastUserDbPushAckTime_utc = DateTime.utc().toISO();
        }
        // #4010 B3: if the stale-unflushed canary fired earlier this session, the first
        // PUSH ack after it means the flagged write reached the server — report the
        // outcome once, with how long the flush took relative to the canary report.
        // pull acks don't count: a pull can bump the sync flag while our push is still
        // pending (the documented heuristic race), so only a push proves the flush.
        if (info.direction === 'push' && sessionStorage.unflushedChangeReported && !sessionStorage.unflushedOutcomeReported) {
          sessionStorage.unflushedOutcomeReported = '1';
          const reportedToFlushMs = elapsedSinceIsoMs(sessionStorage.unflushedChangeReported, DateTime.utc().toMillis());
          console.log(`${logHeader} stale unflushed change flushed ${Math.round(reportedToFlushMs / 1000)}s after the canary report`);
          ctx.telemetry?.trackEvent('sync_stale_unflushed_outcome', {
            outcome: 'flushed',
            reported_to_flush_ms: Math.round(reportedToFlushMs),
          });
        }
      }
      if (shouldUpdateState) {
        ctx.updateStateSyncing();
      }
    })
    .on('paused', info => {
      ctx.flushSyncDiagnostics();

      const isCleanIdle = !info || !info.message;

      // if sync paused cleanly (no error info), reset the auth-error counter —
      // it should reflect only errors that have happened since the most recent
      // successful sync round-trip. lastSyncError is cleared later, AFTER the
      // silent-stall diagnostic has had a chance to capture it.
      if (isCleanIdle) {
        ctx.consecutiveAuthErrors = 0;
      }

      // if this pause was caused by a network error (common on iOS Safari when resuming from background)
      if (info && typeof info === 'object' && info.message === 'Load failed') {
        console.warn(`${logHeader} Sync paused with network error, will restart:`, info);
        // record the network failure so the stale-unflushed-change (429) and
        // checkpoint-drift (3ZD) diagnostics can tell "known network drop" from "silent
        // stall". Without this the branch returns early and lastSyncError stays whatever a
        // prior clean idle nulled it to — usually null — exactly during the flaky-network
        // case those diagnostics must stay quiet for.
        ctx.lastSyncError = { source: 'paused', error: ctx.serializeSyncError(info), timestamp: DateTime.utc().toISO() };
        // restart sync after a short delay to allow network to stabilize
        setTimeout(() => {
          if (ctx.syncHandler) {
            ctx.cancelSync();
            void ctx.startSync(ctx.shouldRestartWithInitialSync());
          }
        }, 2000);
        return;
      }

      // if this pause carries any other network-shaped error, record it the same way before
      // the diagnostics below run (df-280a5f714662). On iOS resume the request that hung
      // across the suspension fails with "The network connection was lost." or "The request
      // timed out."; left unrecorded, the push-stall check reads a healthy environment and
      // reports the clock it started before the app was backgrounded. Only the exact
      // "Load failed" wording above restarts sync; this records and carries on.
      if (!isCleanIdle && isNetworkShapedSyncError(info)) {
        ctx.lastSyncError = { source: 'paused', error: ctx.serializeSyncError(info), timestamp: DateTime.utc().toISO() };
      }

      // if live sync is caught up, stop the fast-lane listener (regular sync handles real-time now)
      ctx.stopRealTimeListener();

      if (ctx.isUserDb()) {
        // detect background sync completion: first successful pause on live sync
        // means it caught up with all historical docs (including old logs skipped by priority sync)
        if (!initial && isCleanIdle) {
          const bgKey = `${ctx.dbBackgroundSyncCompleteKeyPrefix}${ctx.databaseIdLocal}`;
          if (!localStorage[bgKey]) {
            console.log(`${logHeader} Background sync completed (live sync caught up)`);
            localStorage[bgKey] = DateTime.utc().toISO();
          }
        }

        // DatabaseState.Offline is published by the error/denied/auth handlers
        // and by setAllDatabasesStatusOffline (network ping fail) — those are
        // event-driven real failures. The previous heuristic over the change-
        // tracking flags (#3274) misfired during coach impersonation because
        // sync-pulled writes from other devices look like local changes; it has
        // been replaced by the silent-stall diagnostic below.
        if (!initial) {
          if (sessionStorage.lastUserDbChangeTime_utc && !sessionStorage.lastUserDbSyncTime_utc) {
            ctx.reportStaleUnflushedChangeIfNeeded();
          } else if (!ctx.isStateReady()) {
            ctx.updateStateReady();
          }
          // df-ed6d17c8ec6f: unlike the stale-unflushed check above, this one must run even
          // when a pull has bumped lastUserDbSyncTime_utc — a working pull is exactly what
          // hides a persistently-failing push. It reads push-ack liveness, not the sync flag.
          ctx.reportPushStallIfNeeded();
        }
      }

      // if sync paused cleanly, clear lastSyncError now that any silent-stall
      // diagnostic above has had a chance to capture it. clearing it here
      // means any error that happened since the previous clean idle survives
      // long enough to land in the Sentry payload, while still preventing
      // older errors from leaking into the next session's diagnostics.
      if (isCleanIdle) {
        ctx.lastSyncError = null;
      }

      console.log(`${logHeader} Sync paused`, info);
    })
    .on('denied', err => {
      // if IndexedDB connection error, attempt recovery (reload)
      if (ctx.databaseRecovery.interceptDatabaseError(err, 'replication-denied')) {
        return;
      }

      ctx.lastSyncError = { source: 'denied', error: ctx.serializeSyncError(err), timestamp: DateTime.utc().toISO() };
      if (initial) {
        // log as info — assureInitialSyncAsync has app-level retry logic
        console.log(`${logHeader} Sync denied (will retry): `, err);
        // #6554: reports the failed round but keeps reads on the server while online
        ctx.recordInitialSyncRoundFailed();
        ctx.syncTasksCompleted += 1;
        ctx.publishSyncProgress({});
      } else if (ctx.isNonRetryableSyncError(err)) {
        ctx.handleSyncAuthError(err, 'denied', logHeader);
      } else {
        console.log(`${logHeader} Sync denied (will retry): `, err);
        ctx.updateStateOffline();
      }
    })
    .on('error', err => {
      ctx.flushSyncDiagnostics();

      // #5688: an initial pass is a one-shot replication, and PouchDB keeps a
      // one-shot `replicate.from` running after it reports an error — skipping
      // documents and moving the checkpoint past them, so the retry below finds
      // nothing to do. Stop it first, whatever else this handler decides. A
      // two-way sync already cancels itself, and cancelling it again is harmless.
      if (initial) {
        stopFailedOneShotReplication(replication, logHeader);
        // the pass is over, so stop holding it, or a restart outside the retry loop is refused
        // for good. `denied` does not do this: PouchDB carries on after a refused document
        releaseFailedInitialSync(ctx, replication);
      }

      // if IndexedDB connection error, attempt recovery (reload)
      if (ctx.databaseRecovery.interceptDatabaseError(err, 'replication-error')) {
        return;
      }

      ctx.lastSyncError = { source: 'error', error: ctx.serializeSyncError(err), timestamp: DateTime.utc().toISO() };
      if (initial) {
        // log as info — assureInitialSyncAsync has app-level retry logic
        console.log(`${logHeader} Sync error (will retry): `, err);
        // #6554: reports the failed round but keeps reads on the server while online,
        // so a page opened during the retry wait does not read the half-downloaded store
        ctx.recordInitialSyncRoundFailed();
        ctx.syncTasksCompleted += 1;
        ctx.publishSyncProgress({});
      } else if (ctx.isNonRetryableSyncError(err)) {
        ctx.handleSyncAuthError(err, 'error', logHeader);
      } else {
        console.log(`${logHeader} Sync error (will retry): `, err);
        ctx.updateStateOffline();
      }
    })
    .catch(err => {
      // errors already handled by .on('error') EventEmitter above; catch the
      // promise rejection to prevent unhandled rejection noise — but log it,
      // so a rejection that never surfaced as an 'error' event is not zero
      // information (#3987 A2, defect D9)
      console.log(`${logHeader} sync promise rejected (already handled by error event):`, err);
    });
}
